import React from 'react';
import { useI18n } from '../../../contexts/I18nContext';
import { AuditLogEntry, AuditStatus } from '../../../types/audit';
import UnifiedAuditDiff from '../UnifiedAuditDiff';

interface AuditEventRowProps {
  entry: AuditLogEntry;
  expanded: boolean;
  onToggle: () => void;
  /** A click on the actor, action or resource cell filters the list on it. */
  onFilterActor: (actor: string) => void;
  onFilterAction: (action: string) => void;
  onFilterResourceType: (resourceType: string) => void;
}

/** The label of a status, translated. */
export const useStatusLabel = () => {
  const { t } = useI18n();
  return (status: AuditStatus | string): string => {
    if (status === 'success') return t('audit.filters.statusSuccess');
    if (status === 'failed') return t('audit.filters.statusFailed');
    if (status === 'denied') return t('audit.filters.statusDenied');
    return status;
  };
};

/** One event: the row, and when opened the before/after diff with the request details. */
export const AuditEventRow: React.FC<AuditEventRowProps> = ({ entry, expanded, onToggle, onFilterActor, onFilterAction, onFilterResourceType }) => {
  const { formatDateTime, formatRelativeTime, t } = useI18n();
  const statusLabel = useStatusLabel();

  return (
    <>
      <tr className={`tpl-row aud-row${expanded ? ' is-expanded' : ''}`} onClick={onToggle} data-testid={`audit-row-${entry.id}`}>
        <td className="aud-when">
          <span className="aud-when-rel">{formatRelativeTime(entry.timestamp)}</span>
          <span className="tpl-sub">{formatDateTime(entry.timestamp)}</span>
        </td>
        <td>
          <button type="button" className="aud-cell-filter" onClick={(e) => { e.stopPropagation(); if (entry.actor_username) onFilterActor(entry.actor_username); }} title={t('audit.filters.actor')}>
            {entry.actor_username || t('audit.table.anonymous')}
          </button>
        </td>
        <td>
          <button type="button" className="aud-cell-filter mono" onClick={(e) => { e.stopPropagation(); onFilterAction(entry.action); }} title={t('audit.filters.action')}>
            {entry.action}
          </button>
        </td>
        <td className="aud-resource">
          <button type="button" className="type-badge tpl-type aud-type-btn" onClick={(e) => { e.stopPropagation(); onFilterResourceType(entry.resource_type); }} title={t('audit.filters.resourceType')}>
            {entry.resource_type}
          </button>
          {entry.resource_id && <span className="aud-resource-id mono">{entry.resource_id}</span>}
        </td>
        <td><span className={`aud-status is-${entry.status}`}>{statusLabel(entry.status)}</span></td>
        <td>{entry.http_method ? <span className="aud-method">{entry.http_method}</span> : <span className="tpl-muted">–</span>}</td>
        <td className="aud-path mono" title={entry.http_path || ''}>{entry.http_path || '–'}</td>
        <td className="tpl-col-open"><span className={`tpl-chevron aud-chevron-row${expanded ? ' is-open' : ''}`} aria-hidden="true">›</span></td>
      </tr>
      {expanded && (
        <tr className="aud-expanded">
          <td colSpan={8}>
            <div className="aud-detail">
              <UnifiedAuditDiff oldValue={entry.old_value} newValue={entry.new_value} />
              <dl className="aud-meta">
                <div><dt>{t('audit.table.timestamp')}</dt><dd>{formatDateTime(entry.timestamp)}</dd></div>
                {entry.actor_ip && <div><dt>{t('audit.row.ip')}</dt><dd className="mono">{entry.actor_ip}</dd></div>}
                {entry.actor_roles && entry.actor_roles.length > 0 && <div><dt>{t('audit.row.roles')}</dt><dd>{entry.actor_roles.join(', ')}</dd></div>}
                {entry.http_path && <div><dt>{t('audit.table.path')}</dt><dd className="mono">{entry.http_method} {entry.http_path}</dd></div>}
              </dl>
              {entry.error_message && (
                <div className="aud-error-box" role="alert">
                  <strong>{t('audit.row.errorMessage')}</strong> {entry.error_message}
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};
